/**
 * Elegir las opciones al apuntar un plato.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ ESTO SOLO APARECE SI EL PLATO TIENE GRUPOS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Y es la decisión que hace que el modo restaurante siga siendo rápido. Tocar
 * un plato lo apunta al instante, como siempre. La única vez que se interpone
 * una pantalla es cuando ese plato de verdad tiene algo que preguntar.
 *
 * Si el modal saliera siempre —aunque fuera vacío, para «confirmar»— cada
 * pedido pasaría de un toque a dos, toda la noche, por todos los platos. Eso
 * es lo que hace que un mesero deje de usar el sistema y vuelva a la libreta.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  LOS GRUPOS OBLIGATORIOS BLOQUEAN EL BOTÓN, NO AVISAN DESPUÉS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El servidor también lo comprueba —la pantalla se puede saltar— pero si aquí
 * dejáramos apuntar y el error saliera al volver, quien atiende ya cerró el
 * modal y está mirando la mesa. Con el botón apagado y el motivo escrito
 * («Falta elegir: Proteína»), el problema se ve donde se puede arreglar.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  «SIN TOMATE» VA ARRIBA, CUANDO LA VENTANA YA IBA A SALIR (2026-10-27)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Si el plato tiene extras, esta ventana se abre igual: ahí mismo se quitan
 * los ingredientes, sin otra ventana. Un plato sin extras no la abre nunca por
 * esto; su «Quitar ingredientes» está en la línea de la cuenta.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Boton, Cargando, cx } from "@/components/ui";
import { Modal } from "@/components/Modal";
import { opcionesDePlato } from "@/lib/endpoints";
import { formatMoney, toNumber, type FormatoMoneda } from "@/lib/money";
import type { GrupoDeOpciones, IngredienteQuitado, QuitableDeLista } from "@/lib/types";
import { ChipsDeSin, quitadosConNombre } from "./QuitarIngredientes";

/** Un extra elegido, con lo que hace falta para enseñarlo y sumarlo. */
export type ExtraElegidoEnModal = { id: string; nombre: string; precio: string };

export function ModalDeOpciones({
  itemId,
  nombre,
  precioBase,
  formato,
  onCerrar,
  onApuntar,
  accion = "Apuntar",
  descripcion = "Seleccione los extras. Se indican en la comanda de cocina.",
  quitables = [],
}: {
  itemId: string;
  nombre: string;
  precioBase: string;
  formato: FormatoMoneda;
  onCerrar: () => void;
  /**
   * Los ids y, para quien los enseña (la caja), su nombre y precio. Y lo que
   * se pidió quitar («sin tomate»), si el plato lo permite.
   */
  onApuntar: (
    opciones: string[],
    detalle: ExtraElegidoEnModal[],
    sin: IngredienteQuitado[],
  ) => void;
  /** El verbo del botón: «Apuntar» en la mesa, «Agregar» en la caja. */
  accion?: string;
  descripcion?: string;
  /**
   * Lo que se puede quitar de este plato en la variación elegida. Vacío = la
   * sección no sale (la función apagada, o un plato sin receta).
   */
  quitables?: QuitableDeLista[];
}) {
  const [elegidas, setElegidas] = useState<Set<string>>(new Set());
  const [quitados, setQuitados] = useState<Set<string>>(new Set());

  const { data: grupos, isLoading, isError } = useQuery({
    queryKey: ["grupos-del-plato", itemId],
    queryFn: () => opcionesDePlato.gruposDelPlato(itemId),
  });

  function alternar(grupo: GrupoDeOpciones, opcionId: string) {
    setElegidas((previas) => {
      const copia = new Set(previas);

      if (copia.has(opcionId)) {
        copia.delete(opcionId);
        return copia;
      }

      // En un grupo de UNA sola, elegir otra sustituye a la anterior en vez de
      // dar error. Es lo que hace cualquier lista de opciones del mundo, y el
      // mesero que se equivocó de proteína solo tiene que tocar la buena.
      if (grupo.maximo === 1) {
        for (const o of grupo.opciones) copia.delete(o.id);
      } else if (
        grupo.maximo > 1 &&
        grupo.opciones.filter((o) => copia.has(o.id)).length >= grupo.maximo
      ) {
        // ⚠️ Con tope de dos o más, la de más no entra. Antes entraba, y el
        // «demasiadas opciones» llegaba del servidor con la ventana ya cerrada
        // (lo encontró la revisión del 2026-10-27). El tope se lee al lado.
        return previas;
      }
      copia.add(opcionId);
      return copia;
    });
  }

  function alternarQuitado(variantId: string) {
    setQuitados((previos) => {
      const copia = new Set(previos);
      if (copia.has(variantId)) copia.delete(variantId);
      else copia.add(variantId);
      return copia;
    });
  }

  const { extra, falta } = useMemo(() => {
    let suma = 0;
    const pendientes: string[] = [];

    for (const g of grupos ?? []) {
      const cuantas = g.opciones.filter((o) => elegidas.has(o.id));
      for (const o of cuantas) suma += toNumber(o.precio_extra);
      if (cuantas.length < g.minimo) pendientes.push(g.nombre);
    }
    return { extra: suma, falta: pendientes };
  }, [grupos, elegidas]);

  const total = toNumber(precioBase) + extra;
  // ⚠️ Mientras cargan los extras (o si no cargaron) no se apunta: con la
  // lista vacía no habría «falta elegir» que frenara, y el plato saldría sin
  // lo que había que preguntar.
  const sePuede = falta.length === 0 && !isLoading && !isError;

  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo={nombre}
      descripcion={descripcion}
      ancho="sm"
      pie={
        <>
          <Boton variante="secundario" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton
            disabled={!sePuede}
            onClick={() =>
              onApuntar(
                [...elegidas],
                (grupos ?? []).flatMap((g) =>
                  g.opciones
                    .filter((o) => elegidas.has(o.id))
                    .map((o) => ({ id: o.id, nombre: o.nombre, precio: o.precio_extra })),
                ),
                quitadosConNombre(quitables, quitados),
              )
            }
          >
            {/*
              El botón dice el precio FINAL, no «Añadir». Es lo que el mesero
              le va a decir al cliente, y verlo antes de apuntar evita la
              sorpresa al cobrar.
            */}
            {accion} · {formatMoney(String(total), formato)}
          </Boton>
        </>
      }
    >
      {isLoading && <Cargando />}
      {isError && (
        <p className="mb-3 text-xs text-danger">
          No fue posible cargar los extras de este plato. Cierra la ventana e inténtalo de nuevo.
        </p>
      )}

      {quitables.length > 0 && (
        <div className="mb-4 border-b border-line pb-4">
          <p className="mb-2 text-sm font-semibold text-ink">Quitar ingredientes</p>
          <ChipsDeSin quitables={quitables} quitados={quitados} onAlternar={alternarQuitado} />
        </div>
      )}

      <div className="space-y-4">
        {(grupos ?? []).map((g) => {
          const puestas = g.opciones.filter((o) => elegidas.has(o.id)).length;
          const incompleto = puestas < g.minimo;

          return (
            <div key={g.id}>
              <div className="mb-2 flex items-center gap-2">
                <p className="text-sm font-semibold text-ink">{g.nombre}</p>
                {g.minimo > 0 && (
                  <span
                    className={cx(
                      "text-xs",
                      incompleto ? "font-medium text-warning" : "text-muted",
                    )}
                  >
                    {incompleto ? "Falta elegir" : "Completo"}
                  </span>
                )}
                {g.maximo > 1 && <span className="text-xs text-muted">Máximo {g.maximo}</span>}
              </div>

              <div className="flex flex-wrap gap-2">
                {g.opciones.map((o) => {
                  const activa = elegidas.has(o.id);
                  const cuesta = toNumber(o.precio_extra) > 0;
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => alternar(g, o.id)}
                      className={cx(
                        // 44px de alto: esto se toca con el dedo, de pie y con
                        // prisa. Con menos se falla y se pulsa la de al lado.
                        "min-h-11 rounded-xl border px-3 py-2 text-left text-sm transition-colors",
                        activa
                          ? "border-accent bg-accent-soft font-medium text-accent"
                          : "border-line text-ink hover:border-line-strong",
                      )}
                    >
                      {o.nombre}
                      {/*
                        El precio solo cuando cuesta. Poner «+$0,00» en las
                        gratis llena la pantalla de ceros y hace dudar de si
                        se está cobrando algo.
                      */}
                      {cuesta && (
                        <span className="ml-1.5 text-xs opacity-80">
                          +{formatMoney(o.precio_extra, formato)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {!sePuede && (
        <p className="mt-4 text-xs text-warning">
          Falta elegir: {falta.join(", ")}.
        </p>
      )}
    </Modal>
  );
}
